import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLink, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  getFontIconColorPrimary,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  infoIcon,
} from 'globalStyles';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

export enum LearnMoreModes {
  //"Learn more..." with the open-in-new-tab icon, below a message or inside a tooltip
  Default = 1,
  //"Learn" with the learn icon, where the long form takes too much room, such as behind a scene bar title
  Learn = 2,
}

interface ILearnMoreProps {
  learnMoreLink?: string;
  text?: string;
  bold?: boolean;
  mode?: LearnMoreModes;
}

export const LearnMore = (props: ILearnMoreProps) => {
  const { t } = useTranslation(['translation']);
  const theme = getTheme();
  const isLearn = props.mode === LearnMoreModes.Learn;

  if (!props.learnMoreLink) return null;

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={globalStackTokensGapExtraSmall}
      verticalAlign="center"
      styles={{ root: { paddingTop: isLearn ? undefined : 5 } }}
    >
      <IPFontIcon
        iconName={isLearn ? 'D365TalentLearn' : 'OpenInNewTab'}
        style={{ color: theme.palette.themePrimary }}
      />
      <IPText styles={props.bold ? globalTextStylesBold : undefined}>
        <IPLink href={props.learnMoreLink} target="_blank" rel="noopener noreferrer">
          {props.text ||
            (isLearn
              ? t('translation:General.Notifications.Learn')
              : t('translation:General.Notifications.LearnMore'))}
        </IPLink>
      </IPText>
    </IPStack>
  );
};

interface IInfoProps {
  message: string | JSX.Element | undefined;
  learnMoreLink?: string;
}

export const Info = (props: IInfoProps): JSX.Element | null => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const getMessage = (): JSX.Element => {
    if (typeof props.message === 'string') {
      return <IPText>{props.message}</IPText>;
    } else {
      return props.message as JSX.Element;
    }
  };

  const getMessageJSX = (): JSX.Element => {
    return (
      <IPStack>
        <IPStack.Item>{getMessage()}</IPStack.Item>
        <IPStack.Item>
          <LearnMore learnMoreLink={props.learnMoreLink} />
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (!props.message) return null;

  return (
    <IPTooltipHost
      content={getMessageJSX()}
      styles={{ root: { cursor: 'pointer' } }}
      calloutProps={{
        beakWidth: 0,
        // Focus moves into the bubble only to reach the learn-more link, so keyboard users can open it
        // with Enter. Without a link there is nothing to reach, and the focus would land on the first
        // focusable thing in the message instead - a rich-text block is one - and paint the browser's
        // focus ring around it, which reads as a second frame drawn inside the tooltip
        setInitialFocus: Boolean(props.learnMoreLink),
      }}
    >
      <IPFontIcon
        tabIndex={0}
        {...infoIcon}
        style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme) }}
      />
    </IPTooltipHost>
  );
};

interface ITextInfoProps {
  message?: string | JSX.Element;
  learnMoreLink?: string;
  children?: ReactNode;
}

const TextInfo = (props: ITextInfoProps) => {
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
      {props.children}
      <Info message={props.message} learnMoreLink={props.learnMoreLink} />
    </IPStack>
  );
};

export default TextInfo;
